import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  syncChatsFromServer,
  createChatOnServer,
  syncMessageToServer,
  deleteChatOnServer,
  updateChatOnServer,
  setOnlineStatus,
  clearError,
  loadChatsPage,
  loadChatMessages
} from '../../chatSlice';
import { removePlaceholderMessages } from '../../chatSlice';
import { ChatStreamService, flushAudioOutbox } from '../utils/chatApiUtils';
import { MessageProps } from '../screens/dialogs/components/Message';
// import { Chat } from '../chatSlice';
// import { debounce } from '../utils/debounce';

// Функция для конвертации Python dict в JSON
function convertPythonDictToJson(pythonDict: string): string {
  try {
    // Заменяем Python литералы на JSON
    let result = pythonDict
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
      .replace(/\bNone\b/g, 'null');

    // Заменяем одинарные кавычки на двойные, правильно экранируя содержимое
    const tokens = [];
    let inString = false;
    let stringStart = -1;
    let stringChar = '';
    let i = 0;

    while (i < result.length) {
      const char = result[i];
      const prevChar = i > 0 ? result[i - 1] : '';

      // Пропускаем экранированные символы
      if (prevChar === '\\') {
        i++;
        continue;
      }

      if (!inString && char === "'") {
        inString = true;
        stringStart = i;
        stringChar = char;
      } else if (inString && char === stringChar) {
        // Найдена закрывающая кавычка
        const stringContent = result.substring(stringStart + 1, i);
        tokens.push(`"${stringContent.replace(/"/g, '\\"')}"`);
        inString = false;
        stringStart = -1;
      } else if (!inString) {
        tokens.push(char);
      }

      i++;
    }

    // Если строка не была закрыта, добавляем оставшуюся часть
    if (inString && stringStart >= 0) {
      const stringContent = result.substring(stringStart + 1);
      tokens.push(`"${stringContent.replace(/"/g, '\\"')}"`);
    }

    return tokens.join('');
  } catch (error) {
    console.error('Ошибка конвертации Python dict в JSON:', error);
    // Fallback к простой замене
    return pythonDict
      .replace(/'/g, '"')
      .replace(/True/g, 'true')
      .replace(/False/g, 'false')
      .replace(/None/g, 'null');
  }
}

// Глобальный флаг для предотвращения повторной синхронизации
let globalSyncInitialized = false;

export const useChatSync = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { chats, selectedChatId, isLoading, isSyncing, error, isOnline } = useSelector((state: RootState) => state.chat);

  // Локальный кеш для уже загруженных историй сообщений (на время жизни хука),
  // чтобы избежать повторных запросов из-за двойных эффектов/StrictMode.
  const loadedChatsRef = useRef<Set<string>>(new Set());

  // Создаем стабильные ссылки на действия
  const syncChatsFromServerAction = useCallback(() => (dispatch as any)(syncChatsFromServer()), [dispatch]);
  const loadChatsPageAction = useCallback(
    (params: { skip: number; limit: number }) => (dispatch as any)(loadChatsPage(params)),
    [dispatch]
  );
  const loadChatMessagesAction = useCallback(
    (params: { chatId: string; serverChatId: number }) => (dispatch as any)(loadChatMessages(params)),
    [dispatch]
  );
  const createChatOnServerAction = useCallback((payload: { title: string; firstMessage?: MessageProps }) => (dispatch as any)(createChatOnServer(payload)), [dispatch]);
  const syncMessageToServerAction = useCallback((payload: { chatId: string; message: MessageProps }) => (dispatch as any)(syncMessageToServer(payload)), [dispatch]);
  const deleteChatOnServerAction = useCallback((chatId: string) => (dispatch as any)(deleteChatOnServer(chatId)), [dispatch]);
  const updateChatOnServerAction = useCallback((payload: { chatId: string; updateData: { title?: string; description?: string; is_active?: boolean } }) => (dispatch as any)(updateChatOnServer(payload)), [dispatch]);
  const setOnlineStatusAction = useCallback((isOnline: boolean) => dispatch(setOnlineStatus(isOnline)), [dispatch]);
  const clearErrorAction = useCallback(() => dispatch(clearError()), [dispatch]);

  // Сохраняем ссылку на функцию синхронизации в ref
  const initialChatsLoaderRef = useRef(loadChatsPageAction);
  initialChatsLoaderRef.current = loadChatsPageAction;

  // Вспомогательная проверка BudZdorov-флоу с protocolId
  const isBudzdorovProtocolFlow = () => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return !!params.get('protocolId');
    } catch {
      return false;
    }
  };

  // Синхронизация чатов при загрузке приложения
  const syncChats = useCallback(async () => {
    if (isOnline && !isBudzdorovProtocolFlow()) {
      try {
        const result = loadChatsPageAction({ skip: 0, limit: 10 });
        await (result as any).unwrap();
      } catch (error) {
        console.error('Ошибка синхронизации чатов:', error);
      }
    }
  }, [loadChatsPageAction, isOnline]);

  // Создание чата на сервере
  const createChat = useCallback(async (title: string, firstMessage?: MessageProps) => {
    if (isOnline) {
      try {
        const result = createChatOnServerAction({ title, firstMessage });
        const newChat = await (result as any).unwrap();
        return newChat;
      } catch (error) {
        console.error('Ошибка создания чата:', error);
        throw error;
      }
    } else {
      // В офлайн режиме создаем локальный чат
      const localChat = {
        id: `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title,
        lastMessageTime: firstMessage?.time || new Date().toISOString(),
        messages: firstMessage ? [firstMessage] : [],
        createdAt: new Date().toISOString(),
        isDirty: true
      };
      return localChat;
    }
  }, [createChatOnServerAction, isOnline]);

  // Синхронизация сообщения на сервер
  const syncMessage = useCallback(async (chatId: string, message: MessageProps) => {
    if (isOnline) {
      try {
        // console.log('=== ОТПРАВКА СООБЩЕНИЯ НА СЕРВЕР ===');
        // console.log('chatId:', chatId);
        // console.log('message:', message);
        // console.log('message.type:', message.type);
        // console.log('message.json:', message.json);
        // console.log('message.text:', message.text);

        const result = syncMessageToServerAction({ chatId, message });
        await (result as any).unwrap();

        // console.log('✅ Сообщение успешно отправлено на сервер');
      } catch (error) {
        console.error('❌ Ошибка синхронизации сообщения:', error);
        throw error;
      }
    }
  }, [syncMessageToServerAction, isOnline]);

  // Удаление чата на сервере
  const deleteChat = useCallback(async (chatId: string) => {
    if (isOnline) {
      try {
        const result = deleteChatOnServerAction(chatId);
        await (result as any).unwrap();
      } catch (error) {
        console.error('Ошибка удаления чата:', error);
        throw error;
      }
    }
  }, [deleteChatOnServerAction, isOnline]);

  // Обновление чата
  const updateChat = useCallback(async (chatId: string, updateData: { title?: string; description?: string; is_active?: boolean }) => {
    if (isOnline) {
      try {
        const result = updateChatOnServerAction({ chatId, updateData });
        await (result as any).unwrap();
      } catch (error) {
        console.error('Ошибка обновления чата:', error);
        throw error;
      }
    }
  }, [updateChatOnServerAction, isOnline]);

  // Очистка ошибок
  const clearErrors = useCallback(() => {
    clearErrorAction();
  }, [clearErrorAction]);

  // Отслеживание онлайн статуса
  useEffect(() => {
    const handleOnline = () => {
      setOnlineStatusAction(true);
      // При восстановлении соединения: переотправим локальные несинхронизированные сообщения
      try {
        const resend = async () => {
          // Сначала пытаемся выгрузить очередь аудио
          await flushAudioOutbox();
          const dirtyTasks: Array<Promise<any>> = [];
          for (const chat of chats) {
            const chatId = chat.id;
            for (const msg of chat.messages as any[]) {
              if (!msg.serverId && (msg as any).sender === 'user') {
                dirtyTasks.push(
                  (async () => {
                    try {
                      const result = syncMessageToServerAction({ chatId, message: msg });
                      await (result as any).unwrap();
                    } catch (e) {
                      // Оставляем локально; повторим позже
                      console.warn('Повторная отправка сообщения не удалась', e);
                    }
                  })()
                );
              }
            }
          }
          if (dirtyTasks.length > 0) {
            await Promise.allSettled(dirtyTasks);
          }
        // Затем подтянем актуальные данные чатов, не теряя локальные
        try {
          if (!isBudzdorovProtocolFlow()) {
            const result = syncChatsFromServerAction();
            await (result as any).unwrap();
          }
        } catch { }
        };
        resend();
      } catch { }
    };

    const handleOffline = () => {
      setOnlineStatusAction(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setOnlineStatusAction]); // Убираем isOnline и syncChats из зависимостей для предотвращения бесконечного цикла

  // Умная одноразовая синхронизация при загрузке
  useEffect(() => {
    if (isOnline && !globalSyncInitialized && !isLoading && !isSyncing && !isBudzdorovProtocolFlow()) {
      // Проверяем, есть ли уже чаты в localStorage
      const savedChats = localStorage.getItem('chats');
      const lastSyncTime = localStorage.getItem('lastSyncTime');
      const hasExistingChats = savedChats && JSON.parse(savedChats).length > 0;

      // Проверяем, нужно ли синхронизировать (если прошло больше 30 секунд с последней синхронизации)
      const shouldSync = !hasExistingChats || !lastSyncTime ||
        (Date.now() - new Date(lastSyncTime).getTime()) > 30000; // 30 секунд

      if (!shouldSync) {
        globalSyncInitialized = true;
        return;
      }

      console.log('🔄 Синхронизация чатов с сервера (пагинация)...');
      initialChatsLoaderRef.current({ skip: 0, limit: 10 });
      // Устанавливаем глобальный флаг сразу, чтобы предотвратить повторные вызовы
      globalSyncInitialized = true;
    } else if (!isOnline) {
      // Сбрасываем глобальный флаг при переходе в офлайн, чтобы синхронизировать при восстановлении
      globalSyncInitialized = false;
    }
  }, [isOnline, isLoading, isSyncing]);

  // Принудительная синхронизация (игнорирует флаги и время)
  const forceSyncChats = useCallback(async () => {
    if (isOnline && !isBudzdorovProtocolFlow()) {
      console.log('🔄 Принудительная синхронизация чатов...');
      globalSyncInitialized = false; // Сбрасываем флаг для принудительной синхронизации
      try {
        const result = syncChatsFromServerAction();
        await (result as any).unwrap();
        console.log('✅ Принудительная синхронизация завершена');
      } catch (error) {
        console.error('❌ Ошибка принудительной синхронизации:', error);
        throw error;
      }
    }
  }, [isOnline, syncChatsFromServerAction]);

  // Лениво подгружаем историю сообщений при выборе чата (если её ещё нет)
  useEffect(() => {
    if (!selectedChatId || !isOnline) return;

    const chat = chats.find((c: import('../../chatSlice').Chat) => c.id === selectedChatId);
    if (!chat) return;

    // Если уже грузили историю для этого чата в текущем сеансе хука — ничего не делаем
    if (loadedChatsRef.current.has(chat.id)) return;

    // Если сообщения уже есть в кеше, повторно не грузим
    if (chat.messages && chat.messages.length > 0) {
      loadedChatsRef.current.add(chat.id);
      return;
    }

    const serverChatId = chat.serverId ?? parseInt(chat.id, 10);
    if (!serverChatId || Number.isNaN(serverChatId)) return;

    loadedChatsRef.current.add(chat.id);
    loadChatMessagesAction({ chatId: chat.id, serverChatId });
  }, [selectedChatId, chats, isOnline, loadChatMessagesAction]);

  return {
    chats,
    selectedChatId,
    isLoading,
    isSyncing,
    error,
    isOnline,
    syncChats,
    createChat,
    syncMessage,
    deleteChat,
    updateChat,
    clearErrors,
    forceSyncChats
  };
};

// Хук для работы с потоковыми сообщениями
export const useChatStream = (chatId: string | null) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isOnline } = useSelector((state: RootState) => state.chat);
  const streamServiceRef = useRef<ChatStreamService | null>(null);

  // Обработка новых сообщений из потока
  const handleNewMessage = useCallback((serverMessage: any) => {
    // console.log('=== ПОЛУЧЕНО СООБЩЕНИЕ ИЗ SSE ===');
    // console.log('serverMessage:', serverMessage);
    // console.log('serverMessage.type:', serverMessage.type);
    // console.log('serverMessage.json_content:', serverMessage.json_content);
    // console.log('serverMessage.text:', serverMessage.text);

    // Определяем JSON данные для протоколов
    let jsonData = serverMessage.json_content;

    // Если json_content пустое, но это json-сообщение, пытаемся парсить text
    if (!jsonData && serverMessage.type === 'json' && serverMessage.text) {
      try {
        // Сначала пытаемся парсить как есть
        jsonData = JSON.parse(serverMessage.text);
        console.log('Протокол успешно спарсен из text поля');
      } catch (firstError) {
        try {
          // Если не получилось, пытаемся исправить одинарные кавычки на двойные
          const correctedJsonString = serverMessage.text.replace(/'/g, '"');
          jsonData = JSON.parse(correctedJsonString);
          console.log('Протокол успешно спарсен из text поля после исправления кавычек');
        } catch (secondError) {
          try {
            // Если и это не помогло, пытаемся использовать функцию конвертации Python dict
            const convertedJsonString = convertPythonDictToJson(serverMessage.text);
            jsonData = JSON.parse(convertedJsonString);
            console.log('Протокол успешно спарсен из text поля после конвертации Python dict');
          } catch (thirdError) {
            console.error('Ошибка парсинга JSON из text:', firstError);
            console.error('Ошибка после исправления кавычек:', secondError);
            console.error('Ошибка после конвертации Python dict:', thirdError);
            console.error('Проблемный текст:', serverMessage.text.substring(0, 200) + '...');
            jsonData = null;
          }
        }
      }
    }

    // Конвертируем серверное сообщение в локальный формат
    const rawTime = (serverMessage && (serverMessage as any).created_at) ||
      (serverMessage && (serverMessage as any).updated_at) ||
      serverMessage.time;
    const resolvedTime =
      typeof rawTime === 'string' && rawTime && !rawTime.endsWith('Z') && !rawTime.includes('+')
        ? `${rawTime}Z`
        : rawTime;

    const localMessage: MessageProps = {
      sender: serverMessage.sender,
      time: resolvedTime,
      type: serverMessage.type,
      text: serverMessage.text,
      title: serverMessage.title,
      specialty: serverMessage.specialty,
      json: jsonData, // Используем обработанные JSON данные
      serverId: serverMessage.id // Добавляем ID сервера для предотвращения дубликатов
    };

    // console.log('=== ЛОКАЛЬНОЕ СООБЩЕНИЕ ===');
    // console.log('localMessage:', localMessage);
    // console.log('localMessage.json:', localMessage.json);

    // Удаляем placeholder и добавляем сообщение в чат
    if (chatId) {
      // Локальный placeholder "Ожидаем распознавание..." добавляется без serverId,
      // поэтому при любом реальном сообщении по аудио из SSE нужно его убрать.
      if (
        localMessage.type === 'audio' ||
        (localMessage.type === 'text' && localMessage.title === 'Распознанный текст') ||
        (typeof localMessage.title === 'string' && /распозна/i.test(localMessage.title))
      ) {
        dispatch(removePlaceholderMessages({ chatId }));
      }
      dispatch({
        type: 'chat/addMessageToChat',
        payload: { chatId, message: localMessage }
      });
    }
  }, [dispatch, chatId]);

  // Обработка ошибок потока
  const handleStreamError = useCallback((error: any) => {
    console.error('Ошибка потока чата:', error);
  }, []);

  // Обработка heartbeat
  const handleHeartbeat = useCallback(() => {
    // Можно добавить логику для отслеживания активности соединения
  }, []);

  // Запуск/остановка потока
  useEffect(() => {
    if (chatId && isOnline) {
      const serverChatId = parseInt(chatId);
      if (!isNaN(serverChatId)) {
        streamServiceRef.current = new ChatStreamService(
          serverChatId,
          handleNewMessage,
          handleStreamError,
          handleHeartbeat
        );
        streamServiceRef.current.start();
      }
    }

    return () => {
      if (streamServiceRef.current) {
        streamServiceRef.current.stop();
        streamServiceRef.current = null;
      }
    };
  }, [chatId, isOnline, handleNewMessage, handleStreamError, handleHeartbeat]);

  const isConnected = streamServiceRef.current?.isConnected() || false;

  return {
    isConnected,
    startStream: () => streamServiceRef.current?.start(),
    stopStream: () => streamServiceRef.current?.stop()
  };
};

// Хук для автоматической синхронизации при восстановлении соединения (зарезервирован)
export const useAutoSync = () => {
  // пусто — логика будет добавлена в будущем
};
